import {networkErrorLink, onceWhileShown} from './graphql/error-link';
import {BannerService} from './banner/banner.service';
import {ApplicationConfig, inject, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, Router, withDebugTracing, withHashLocation} from '@angular/router';
import {provideNamedApollo} from 'apollo-angular';
import {routes} from './app.routes';
import {OAuthService, OAuthStorage, provideOAuthClient} from 'angular-oauth2-oidc';
import {environment} from '../environments/environment';
import {HttpLink} from 'apollo-angular/http';
import {ContextService} from './services/context.service';
import {ApolloLink, InMemoryCache, InMemoryCacheConfig, TypePolicy} from '@apollo/client/core';
import {LocalState} from '@apollo/client/local-state';
import {HttpHeaders, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {DEFAULT_DIALOG_CONFIG} from '@angular/cdk/dialog';
import {DIALOG_DEFAULTS} from './dialog-defaults';
import {Po2024Service} from './services/po-2024.service';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideNgIdle} from '@ng-idle/core';
import {FullscreenOverlayContainer, OverlayContainer} from '@angular/cdk/overlay';
import {from, switchMap} from 'rxjs';
import {ApiTokenService} from './services/api-token.service';
import {TokenRefreshService} from './services/token-refresh.service';
import {retryAfterRefreshLink} from './auth-links';
import {poItemTypePolicy} from './po-item-type-policy';
import {awaitProcessSwitchLink} from './await-process-switch-link';

function routerTraceEnabled(): boolean {
  try {
    return localStorage.getItem('router-trace') === '1';
  } catch {
    return false;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    // localStorage 'router-trace' = '1' logs every router event to the console, from the very first
    // navigation -- the only way to trace the OAuth callback load, which starts before any devtools
    // snippet could be injected. Off unless set.
    provideRouter(routes, withHashLocation(), ...(routerTraceEnabled() ? [withDebugTracing()] : [])),
    provideAnimations(),
    provideNgIdle(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: LocationStrategy, useClass: HashLocationStrategy
    },
    {
      provide: OAuthStorage, useValue: localStorage
    },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    {provide: DEFAULT_DIALOG_CONFIG, useValue: DIALOG_DEFAULTS},
    provideNamedApollo(() => {
        const httpLink = inject(HttpLink);
        const apiToken = inject(ApiTokenService);
        const oAuthService = inject(OAuthService);
        const contextService = inject(ContextService);
        const tokenRefresh = inject(TokenRefreshService);
        const router = inject(Router);
        const banner = inject(BannerService);
        // Outermost on every client: sees what the 401 retry could not fix.
        const networkErrors = networkErrorLink(onceWhileShown(message => banner.open(message, ['Dismiss'])));
        // Refresh the login first (the library's timer can fire after it expired), then take the API token
        // from it: for above users that re-exchanges at federate once the refreshed id_token differs.
        const authMiddleware = new ApolloLink((operation, forward) =>
          from(tokenRefresh.ensureFresh().then(() => apiToken.accessToken())).pipe(switchMap(accessToken => {
          let headers = operation.getContext().headers || new HttpHeaders();
          headers = headers.set('Authorization', accessToken);
//          console.log('Appsync request',  contextService.organization())
//          console.log('operation', operation.operationName)
          if (contextService.organization() && !headers.get('X-Castlabs-Organization')) {

            headers = headers.set('X-Castlabs-Organization', contextService.organization().id);
          }

          operation.setContext({
            headers: headers
          });

          return forward(operation);
        })));

        const retryAfterRefresh = retryAfterRefreshLink(
          () => tokenRefresh.refresh(),
          () => tokenRefresh.signIn());

        function createCache(organizationService: ContextService) {
          const generateCacheKey = (args) => {
            if (organizationService.organizationId()) {
              if (!args) {
                return organizationService.organizationId();
              } else {
                return `${organizationService.organizationId()}:${JSON.stringify(args)}`;
              }
            }
            if (!args) {
              return null;
            } else {
              return JSON.stringify(args);
            }
          };
          const headerAware = {
            keyArgs: false,
            read(existing, {args}) {
              const key = generateCacheKey(args)
              return existing ? existing[key] : undefined;
            },
            merge(existing = {}, incoming, {args}) {
              const key = generateCacheKey(args);
              return {
                ...existing,
                [key]: incoming,
              };
            },
          } as TypePolicy;

          const cacheParams = {

            typePolicies: {
              Query: {
                fields: {
                  roots: headerAware,
                  airlines: headerAware,
                  policies: headerAware,
                  list_POs: headerAware,
                }
              },
              File: {
                fields: {
                  archive: {
                    merge: true,
                  },
                },
              },
              POItem: poItemTypePolicy,
            },
          } as InMemoryCacheConfig;
          return new InMemoryCache(cacheParams);
        }

        const workflowCache = createCache(contextService);
        return {
          'default': {
            cache: createCache(contextService),
            devtools: { enabled: true },
            link: ApolloLink.from([
              networkErrors,
              retryAfterRefresh,
              authMiddleware,
              httpLink.create({
                uri: environment.graphql
              })])
          },
          'workflow': {
            cache: workflowCache,
            localState: new LocalState(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              networkErrors,
              // VTK-3094: until a restarted/published PO item points at its new process, polls must not put
              // the row back to the previous run.
              awaitProcessSwitchLink(() => workflowCache),
              retryAfterRefresh,
              authMiddleware,
              httpLink.create({
              uri: environment.workflow_graphql
            })])
          },
          // Monitoring. Its queries return aggregates over a time window, so the default cache
          // would happily serve a stale window forever -- the monitoring components fetch
          // network-only and poll instead.
          'status': {
            cache: new InMemoryCache(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              networkErrors,
              retryAfterRefresh,
              authMiddleware,
              httpLink.create({
                uri: environment.status_graphql
              })])
          },
          // Signiant Connectors, both directions. Portal session status and
          // import processes change out from under us, so this client uses a
          // plain cache and the components fetch network-only.
          'signiant': {
            cache: new InMemoryCache(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              networkErrors,
              retryAfterRefresh,
              authMiddleware,
              httpLink.create({
                uri: environment.signiant_graphql
              })])
          },
          // Aspera transfers (content-acq-aspera). Process state changes under us, so the components fetch
          // network-only, as for signiant.
          'aspera': {
            cache: new InMemoryCache(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              networkErrors,
              retryAfterRefresh,
              authMiddleware,
              httpLink.create({
                uri: environment.aspera_graphql
              })])
          }
        }
      }
    ),
    provideOAuthClient({
      resourceServer: {
        sendAccessToken: false,
      }
    }),
    {
      provide: 'above_default',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowDefault'),
      deps: [OAuthService]
    },
    {
      provide: 'vod_default',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
    {
      provide: 'vod_repackaging',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
    {
      provide: 'vod_display_interactive',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
    {
      // Watermark-only passthrough delivery. The action is the one the mutation is actually
      // authorized under -- it decides which folders may be added as items -- while the tab itself is
      // gated on `singleframewatermarking:Demo`.
      provide: 'vod_passthrough_sffw',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
  ]
};
